import { useRef, type Ref } from 'react';
import equal from 'fast-deep-equal';
import { Box, Button, Dialog, DialogContent, DialogHeader } from '@ff-it/ui';
import { CheckField, Form, FormError, Submit, TextField, useField, useForm, type SubmissionErrors } from '@ff-it/form';
import { builderWorkspaceHeader } from 'components/BuilderWorkspace';
import { ExportBuilder } from './ExportBuilder';
import { requiredColumns, validateColumns } from './model';
import type { ExportColumn, ExportFieldGroup } from './types';
import * as styles from './ExportBuilderDialog.css';

export type ExportBuilderDialogProps = {
  open: boolean;
  groups: ExportFieldGroup[];
  columns: ExportColumn[];
  onApply: (columns: ExportColumn[], isDefault: boolean) => SubmissionErrors | void | Promise<SubmissionErrors | void>;
  onClose: () => void;
  onSaveAs?: (columns: ExportColumn[], name: string, isDefault: boolean) => Promise<SubmissionErrors | void>;
  onUpdate?: (columns: ExportColumn[], isDefault: boolean) => Promise<SubmissionErrors | void>;
  preset?: {
    name: string;
    columns?: ExportColumn[];
    caption: string;
    isDefault: boolean;
    canChangeDefault?: boolean;
    onDelete?: () => Promise<SubmissionErrors | void>;
  };
  busy?: boolean;
};

type DraftProps = Omit<ExportBuilderDialogProps, 'open'> & { searchRef: Ref<HTMLInputElement> };
type DraftValues = {
  columns: ExportColumn[];
  name: string;
  is_default: boolean;
  new_is_default: boolean;
  mode: 'edit' | 'create';
  action: 'apply' | 'update' | 'delete';
};

function Draft({ groups, onClose, onSaveAs, onUpdate, preset, busy, searchRef }: DraftProps): JSX.Element {
  const form = useForm<DraftValues>();
  const { input: mode } = useField<DraftValues['mode']>('mode', { subscription: { value: true } });
  const {
    meta: { dirty: defaultChanged },
  } = useField<boolean>('is_default', { subscription: { dirty: true } });
  const {
    input,
    meta: { dirty: columnsChanged, error },
  } = useField<ExportColumn[]>('columns', {
    subscription: { value: true, dirty: true, error: true },
    isEqual: equal,
    validate: (columns) => validateColumns(groups, columns),
  });
  const draft = input.value;
  const presetChanged = preset?.columns ? !equal(draft, preset.columns) : columnsChanged;
  const creating = mode.value === 'create';
  const selectedHeader = (
    <Box pb="2" flexShrink={0}>
      <Box textSize="xs" color="secondary" mb="1">
        {creating ? 'New preset' : (preset?.caption ?? 'Current columns')}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="3"
        mb="2"
        style={{ minHeight: '38px' }}
      >
        {creating ? (
          <TextField
            name="name"
            className="mb-0 flex-fill"
            size="md"
            aria-label="Preset name"
            placeholder="Preset name"
            disabled={busy}
            {...{ autoFocus: true, maxLength: 100 }}
            validate={(value) => (value?.trim() ? undefined : 'Enter a preset name.')}
          />
        ) : (
          <Box as="h3" flex="auto" minWidth="0" textSize="lg" m="0">
            {preset?.name ?? 'Custom'}
          </Box>
        )}
      </Box>
    </Box>
  );
  const actions = (
    <Box minWidth="0">
      <FormError />
      {error && (
        <Box as="p" m="0" mb="2" color="danger" textSize="sm" role="alert">
          {error}
        </Box>
      )}
      <Box display="flex" alignItems="center" gap="3" flexWrap="wrap">
        {!creating && preset?.onDelete && (
          <Button
            type="button"
            variant="danger"
            size="sm"
            aria-label={`Delete ${preset.name}`}
            disabled={busy || columnsChanged || defaultChanged}
            onClick={() => {
              if (!window.confirm(`Delete preset “${preset.name}”?`)) return;
              form.change('action', 'delete');
              void form.submit();
            }}
          >
            Delete
          </Button>
        )}
        <Box ml="auto" display="flex" alignItems="center" gap="2" flexWrap="wrap">
          {(creating || (preset && (preset.canChangeDefault || preset.isDefault))) && (
            <CheckField
              name={creating ? 'new_is_default' : 'is_default'}
              fieldLabel="Default preset"
              size="sm"
              disabled={busy || (!creating && !preset?.canChangeDefault)}
            />
          )}
          {!creating && onUpdate && (
            <Submit
              variant="primary"
              size="sm"
              disabled={busy || !!error || (!presetChanged && !defaultChanged)}
              onClick={() => form.change('action', 'update')}
            >
              Update {preset?.name ?? 'preset'}
            </Submit>
          )}
          {onSaveAs && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy || (!creating && !!error)}
              onClick={() => mode.onChange(creating ? 'edit' : 'create')}
            >
              {creating ? 'Back' : 'Save as new'}
            </Button>
          )}
          {creating ? (
            <Submit variant="primary" size="sm" disabled={busy || !!error}>
              Create preset
            </Submit>
          ) : (
            <Submit
              variant={onUpdate ? 'secondary' : 'primary'}
              size="sm"
              disabled={busy || !!error}
              onClick={() => form.change('action', 'apply')}
            >
              Apply
            </Submit>
          )}
        </Box>
      </Box>
    </Box>
  );
  return (
    <>
      <DialogHeader className={builderWorkspaceHeader} title="Export columns" gap="2">
        <Box as="span" color="secondary" textSize="sm" aria-label={`${draft.length} columns selected`}>
          {draft.length} columns
        </Box>
      </DialogHeader>
      <ExportBuilder
        groups={groups}
        columns={draft}
        onChange={input.onChange}
        disabled={busy}
        searchRef={searchRef}
        searchActions={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => input.onChange(requiredColumns(groups))}
          >
            Clear
          </Button>
        }
        selectedHeader={selectedHeader}
        workspace={{
          close: (
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={onClose}>
              Close
            </Button>
          ),
          actions,
          footerLabel: 'Export column actions',
        }}
      />
    </>
  );
}

export function ExportBuilderDialog({ open, ...props }: ExportBuilderDialogProps): JSX.Element {
  const searchRef = useRef<HTMLInputElement>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !props.busy) props.onClose();
      }}
    >
      <DialogContent className={styles.dialog} p="0" gap="0" minWidth="0" overflow="hidden" initialFocus={searchRef}>
        {open && (
          <Form<DraftValues>
            initialValues={{
              columns: props.columns,
              name: '',
              is_default: props.preset?.isDefault ?? false,
              new_is_default: false,
              mode: 'edit',
              action: 'apply',
            }}
            onSubmit={async ({ columns, name, is_default, new_is_default, mode, action }) => {
              let errors;
              if (mode === 'create') errors = await props.onSaveAs?.(columns, name.trim(), new_is_default);
              else if (action === 'update') errors = await props.onUpdate?.(columns, is_default);
              else if (action === 'delete') errors = await props.preset?.onDelete?.();
              else errors = await props.onApply(columns, is_default);
              if (!errors) props.onClose();
              return errors;
            }}
            display="contents"
            renderErrors={false}
            noValidate
            autoComplete="off"
          >
            <Draft {...props} searchRef={searchRef} />
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
